/**
 * Board-grade keys — the FORMAT of `question.boardGrades`.
 *
 *   'cbse:10'  → the CBSE class-10 pool
 *   'jee'      → board-wide exam-track relevance (JEE/NEET)
 *   'jee:11'   → the same track, narrowed to a grade
 *
 * This is the one definition of the key format. It lives here, in the
 * dependency-free shared package, because BOTH sides need it: the backend
 * (schema, write funnel, seeder, serving gate) and the authoring UI, which
 * edits pool membership directly. A second parser written in the UI would fork
 * the format, which is the failure this module exists to prevent.
 *
 * What is deliberately NOT here: anything that needs zod or backend types, and
 * anything that is POLICY rather than format — how keys are derived from a
 * question's legacy tags, what a student's profile grants, what the write
 * funnel does. Those stay in
 * `functions/src/shared/curriculum/boardGradeKeys.ts`, which re-exports this
 * module so backend callers keep one import path.
 *
 * See docs/FeatureDesigns/question-pools-and-entitlement/question-pools-and-programs-target.md §3.
 */

import { ALL_PROGRAMME_OPTIONS, GRADE_OPTIONS, gradesForProgramme, isGradeValidForProgramme, type GradeType, type ProgrammeType } from './academicTypes';

/** Structured view of pool membership for UIs/consumers — DERIVED from keys, never stored. */
export interface BoardGrade {
    board: ProgrammeType;
    /** Omitted when the board is held board-wide only ('jee' with no graded key). */
    grades?: GradeType[];
    /** Minted course levels ('calculus_ab' from 'ap:calculus_ab') — a programme
     *  without classes. Kept apart from `grades` because they are not classes and
     *  must not be offered as such, and kept at all so the UI round-trip does not
     *  drop a question's pool. */
    levels?: string[];
    /** A bare board key is present ('jee'), granting the board as a whole. Independent
     *  of `grades`: an exam track normally carries both ('jee' + 'jee:11'), and losing
     *  either half on a UI round-trip silently changes what the question serves. */
    boardWide?: boolean;
}

/** Built from the FULL vocabulary, base plus extended. A pool key is exactly
 *  where an extended programme has to be expressible: the base/extended split
 *  governs what a picker OFFERS, not what a stored key may say, and a parser
 *  that knew only the base five would read every `ap:…` key as malformed. */
const BOARDS = new Map<string, ProgrammeType>(ALL_PROGRAMME_OPTIONS.map(b => [b.toLowerCase(), b]));
const GRADES = new Set<string>(GRADE_OPTIONS);

/**
 * Exam tracks whose ENTITLEMENT is board-wide: a JEE student is granted the
 * whole track, not one grade of it, so grants carry the bare key ('jee').
 *
 * A track question still carries a graded key too ('jee:11') whenever its grade
 * is known. The bare key answers "may this student see it"; the graded key
 * answers "is this a grade-11 question" — two different questions that the
 * original gradeless-only model collapsed into one. Without the graded key a
 * track question is unreachable by any grade-scoped query.
 */
export const EXAM_TRACKS: ReadonlySet<ProgrammeType> = new Set(['JEE', 'NEET'] as ProgrammeType[]);

/** Key for one (board, grade) pool; grade omitted for a board-wide track key. */
export function boardGradeKey(board: string, grade?: string): string {
    return grade ? `${board.toLowerCase()}:${grade}` : board.toLowerCase();
}

/** A level token that is not a school grade — `calculus_ab` in `ap:calculus_ab`.
 *  Shape only: whether such a curriculum exists is checked at the WRITE paths,
 *  which can read the curricula; this module is dependency-free and cannot. */
const LEVEL_TOKEN = /^[a-z0-9_-]+$/;

/**
 * Parse a key back to its (board, level). Null when the key is malformed or the
 * board is unknown. Case-insensitive.
 *
 * The second segment is a LEVEL: a school grade for a programme that has
 * classes (`cbse:10`), or a minted token for one that does not
 * (`ap:calculus_ab`, `icse:11-literature` — see
 * docs/FeatureDesigns/modules-curriculum-topics-concepts/curriculum-identity-and-eligibility-target.md §4).
 * Still two segments: `a:b:c` remains invalid.
 *
 * Callers that mean a school grade must test `isGradeLevel` rather than assume —
 * `grade` is typed loosely here precisely so that assumption cannot be made
 * silently.
 */
export function parseBoardGradeKey(key: string): { board: ProgrammeType; grade?: string } | null {
    if (typeof key !== 'string') return null;
    const [boardPart, gradePart, ...excess] = key.trim().toLowerCase().split(':');
    if (excess.length > 0) return null;
    const board = BOARDS.get(boardPart);
    if (!board) return null;
    if (gradePart === undefined) return { board };
    if (!LEVEL_TOKEN.test(gradePart)) return null;
    // A grade the programme does not cover names a pool that cannot exist
    // (`ib:7`). A minted level is not a grade and is not checked here.
    if (GRADES.has(gradePart) && !isGradeValidForProgramme(board, gradePart)) return null;
    return { board, grade: gradePart };
}

/** True when a parsed level is a school grade (`'10'`) rather than a minted
 *  course token (`'calculus_ab'`). The grade-shaped readers gate on this. */
export function isGradeLevel(level: string | undefined | null): level is GradeType {
    return !!level && GRADES.has(level);
}

export function isValidBoardGradeKey(key: string): boolean {
    return parseBoardGradeKey(key) !== null;
}

/**
 * Canonicalize a boardGrades array for storage: trim, lowercase, dedupe,
 * preserve first-seen order. Throws on any key outside the valid vocabulary —
 * a bad key silently dropping a question from a pool is exactly the failure
 * mode this field must not have.
 */
export function normalizeBoardGradeKeys(keys: readonly string[]): string[] {
    const out = new Set<string>();
    for (const raw of keys) {
        const parsed = parseBoardGradeKey(raw);
        if (!parsed) throw new Error(`normalizeBoardGradeKeys: invalid board-grade key '${raw}'`);
        out.add(boardGradeKey(parsed.board, parsed.grade));
    }
    return [...out];
}

/** Group keys into the structured per-board view for UIs: 'cbse:9','cbse:11','jee'
 *  → [{board:'CBSE', grades:['9','11']}, {board:'JEE', boardWide:true}]. Invalid keys throw. */
export function groupBoardGradeKeys(keys: readonly string[]): BoardGrade[] {
    const byBoard = new Map<ProgrammeType, { grades: GradeType[]; levels: string[]; boardWide: boolean }>();
    for (const key of normalizeBoardGradeKeys(keys)) {
        const { board, grade } = parseBoardGradeKey(key)!;
        const row = byBoard.get(board) ?? { grades: [], levels: [], boardWide: false };
        // A minted course level is not a class and must not be rendered as one,
        // but it still has to survive the round-trip — a picker that read it and
        // wrote back would otherwise silently drop the question's pool.
        if (isGradeLevel(grade)) row.grades.push(grade);
        else if (grade) row.levels.push(grade);
        else row.boardWide = true;
        byBoard.set(board, row);
    }
    return [...byBoard.entries()].map(([board, { grades, levels, boardWide }]) => {
        const row: BoardGrade = { board };
        if (grades.length) row.grades = grades;
        if (levels.length) row.levels = levels;
        if (boardWide) row.boardWide = true;
        return row;
    });
}

/** Flatten the structured view back to keys (UI writes, seeder output). */
export function toBoardGradeKeys(rows: readonly BoardGrade[]): string[] {
    const keys = new Set<string>();
    for (const row of rows) {
        if (row.boardWide || (!row.grades?.length && !row.levels?.length)) keys.add(boardGradeKey(row.board));
        for (const g of row.grades ?? []) keys.add(boardGradeKey(row.board, g));
        for (const l of row.levels ?? []) keys.add(boardGradeKey(row.board, l));
    }
    return [...keys];
}

/**
 * The grades a key set covers — the back-compat projection of `class`
 * (question-pools-and-entitlement/question-class-retirement-implplan.md step E).
 *
 *   ['cbse:9','icse:10','jee','jee:9'] → ['9','10']
 *
 * `class` stops being an authority at step E and becomes THIS: a derived
 * restatement of `boardGrades` for readers not yet migrated. Bare track keys
 * contribute nothing — they make no grade claim — so a question keyed only
 * `jee` projects to [], which callers must treat as "no projection available"
 * rather than "no grades", or writing it back would erase a real `class`.
 *
 * Sorted numerically so the stored array is stable across writes; an unstable
 * order would make every write look like a change to the sync differ.
 */
export function gradesFromBoardGradeKeys(keys: readonly string[] | undefined | null): GradeType[] {
    const out = new Set<GradeType>();
    for (const raw of keys ?? []) {
        const parsed = parseBoardGradeKey(raw);
        // A minted course level (`ap:calculus_ab`) makes no grade claim, so it
        // contributes nothing here — the same rule a bare track key follows.
        if (isGradeLevel(parsed?.grade)) out.add(parsed!.grade as GradeType);
    }
    return [...out].sort((a, b) => Number(a) - Number(b));
}

/**
 * Every board named by these keys — the BOARD half of the same restatement.
 *
 * `targetExams` is to the boards what `class` is to the grades: a projection of
 * `boardGrades` kept for readers not yet migrated, overwritten by the write
 * funnel so the two can never disagree. It had been author-set instead, and
 * 1156 questions drifted — always by over-claiming, because narrowing a
 * question's keys during curriculum certification never touched it.
 *
 * Unlike the grade projection, bare track keys DO contribute: `jee` names JEE.
 * So this returns [] only for an empty key set, and callers may write it back
 * directly — there is no "no projection available" case to guard.
 *
 * Ordered by ALL_PROGRAMME_OPTIONS rather than alphabetically, so the stored
 * array is stable across writes and an unstable order does not make every write
 * look like a change to the sync differ. The full list, not the base five: a
 * question keyed `ap:calculus_ab` names AP, and filtering against the base list
 * would drop it from its own projection.
 */
export function boardsFromBoardGradeKeys(keys: readonly string[] | undefined | null): ProgrammeType[] {
    const out = new Set<ProgrammeType>();
    for (const raw of keys ?? []) {
        const parsed = parseBoardGradeKey(raw);
        if (parsed) out.add(parsed.board);
    }
    return ALL_PROGRAMME_OPTIONS.filter(b => out.has(b as ProgrammeType)) as ProgrammeType[];
}

/**
 * Every key that means "this question is for grade G", across all boards that
 * cover G:
 *   '9' → ['jee:9','neet:9','icse:9','cbse:9','state:9','ib:9','cambridge:9', …]
 *
 * The key-model replacement for `ARRAY_CONTAINS(c.class, G)`. Grade narrowing is
 * a DIFFERENT operation from pool entitlement — it says nothing about which
 * board the caller is entitled to, so it enumerates them all and lets the
 * entitlement gate do its own (separate) filtering.
 *
 * Returns [] for a grade outside the vocabulary, which callers must read as
 * "no grade filter", never as "matches nothing" — an unknown grade in a query
 * string must not blank the catalog.
 */
export function gradeScopeKeys(grade: string | number | undefined | null): string[] {
    const g = grade == null ? '' : String(grade);
    if (!GRADES.has(g)) return [];
    return ALL_PROGRAMME_OPTIONS
        .filter(board => isGradeValidForProgramme(board, g))
        .map(board => boardGradeKey(board, g));
}

/**
 * Every key that means "this question is for board B", across all grades:
 *   'cbse' → ['cbse', 'cbse:1', … 'cbse:12']
 *
 * The mirror of {@link gradeScopeKeys}, and the bare key is in it on purpose: a
 * track question whose grade is unknown carries `jee` alone, and a board filter
 * that enumerated only the graded keys would report it as belonging to no board
 * — while `boardsFromBoardGradeKeys` says it belongs to this one.
 *
 * Returns [] for a board outside the vocabulary, which callers must read as "no
 * board filter", never as "matches nothing".
 */
export function boardScopeKeys(board: string | undefined | null): string[] {
    const b = BOARDS.get((board ?? '').trim().toLowerCase());
    if (!b) return [];
    return [boardGradeKey(b), ...gradesForProgramme(b).map(g => boardGradeKey(b, g))];
}
